import {
  CircularProgress,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { firstBy } from "thenby";

import type {
  ExtendedVisionUser,
  GetIncidentWorkItemResponse,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";

import type { apiHooks } from "../../api/index.js";
import { DesktopIncidentTable } from "./DesktopIncidentTable/DesktopIncidentTable.js";
import { IncidentList, IncidentListModeOptions } from "./IncidentList.js";
import { isUserActiveInIncident } from "./isUserActiveInIncident.js";

export const HomePageInner = ({
  incidentWorkItems,
  user,
  refetchWorkItems,
}: {
  incidentWorkItems: GetIncidentWorkItemResponse | undefined;
  user: ExtendedVisionUser | undefined;
  refetchWorkItems: ReturnType<
    typeof apiHooks.incidentWorkItem.getMyActiveIncidentWorkItems
  >[1];
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const incidents = Array.isArray(incidentWorkItems?.workItems)
    ? (incidentWorkItems.workItems as IncidentWorkItemWithContext[])
    : [];

  if ([user, incidents].some((x) => x === undefined)) {
    return <CircularProgress />;
  }

  const userId = user?.id;
  const isLoading = incidentWorkItems === undefined;
  const myIncidents = incidents
    .filter((incident) => isUserActiveInIncident(incident, userId))
    .toSorted(
      firstBy((incident) => incident.incidentWorkItem.createdAt, "desc"),
    );

  const transferredIncidents = incidents
    .filter((incident) => !isUserActiveInIncident(incident, userId))
    .toSorted(
      firstBy((incident) => incident.incidentWorkItem.createdAt, "desc"),
    );

  if (isDesktop) {
    return (
      <DesktopIncidentTable
        isLoading={isLoading}
        user={user}
        myIncidents={myIncidents}
        transferredIncidents={transferredIncidents}
        refetchWorkItems={refetchWorkItems}
      />
    );
  }

  return (
    <>
      <IncidentList
        incidentWorkItems={myIncidents}
        mode={IncidentListModeOptions.myIncidents}
        isLoading={isLoading}
        userId={userId}
        refetchWorkItems={refetchWorkItems}
      />
      <Divider />
      <IncidentList
        incidentWorkItems={transferredIncidents}
        mode={IncidentListModeOptions.transferredIncidents}
        isLoading={isLoading}
        userId={userId}
        refetchWorkItems={refetchWorkItems}
      />
    </>
  );
};

export const HomePageIncidents = ({
  user,
  incidentWorkItems,
  refetchWorkItems,
}: {
  user: ExtendedVisionUser | undefined;
  incidentWorkItems: GetIncidentWorkItemResponse | undefined;
  refetchWorkItems: ReturnType<
    typeof apiHooks.incidentWorkItem.getMyActiveIncidentWorkItems
  >[1];
}) => {
  const render = ({
    incidentWorkItems,
    user,
    refetchWorkItems,
  }: {
    incidentWorkItems: GetIncidentWorkItemResponse | undefined;
    user: ExtendedVisionUser | undefined;
    refetchWorkItems: ReturnType<
      typeof apiHooks.incidentWorkItem.getMyActiveIncidentWorkItems
    >[1];
  }) => {
    return (
      <HomePageInner
        incidentWorkItems={incidentWorkItems}
        user={user}
        refetchWorkItems={refetchWorkItems}
      />
    );
  };

  return render({ user, incidentWorkItems, refetchWorkItems });
};
